import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Droplet } from "lucide-react";

const GlucoseWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const glucoseTab = tabs.find((tab) => tab.id === "glucose");
    const widget = glucoseTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget - Status display */}
            <div className="bg-gradient-to-br from-white/80 via-rose-50/30 to-rose-100/20 backdrop-blur-xl rounded-3xl p-4 shadow-2xl border border-rose-200/40 mb-4 hover:shadow-rose-200/30 hover:shadow-2xl transition-all duration-500 group">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                        <motion.div
                            className={`me-3 p-2 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl shadow-lg`}
                            animate={{ scale: [1, 1.05, 1], rotate: [0, 2, 0, -2, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            whileHover={{ scale: 1.1, rotate: 10 }}
                        >
                            <Droplet className="w-6 h-6 text-white" />
                        </motion.div>
                        <div>
                            <h5 className="text-rose-900 font-semibold text-lg">{widget.title}</h5>
                            <p className="text-rose-600 text-xs">{widget.subtitle}</p>
                        </div>
                    </div>
                    <motion.div
                        className="bg-gradient-to-r from-rose-500 to-red-500 text-white px-4 py-2 rounded-2xl font-bold shadow-lg"
                        initial={{ x: 30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        whileHover={{ scale: 1.05 }}
                    >
                        {widget.status}
                    </motion.div>
                </div>

                {/* Glucose status graphic display */}
                <div className="relative mb-3 overflow-hidden rounded-2xl bg-gradient-to-br from-rose-50/50 via-white/60 to-red-50/30 backdrop-blur-sm p-4 group-hover:shadow-inner transition-all duration-500">
                    <div className="relative flex items-center justify-center h-28">
                        {/* Status display with wave animation */}
                        <motion.div className="relative w-full max-w-xs" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
                            {/* Status backgrounds */}
                            <div className="flex justify-between items-center mb-6">
                                <motion.div className="flex flex-col items-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
                                    <div className="w-3 h-3 bg-blue-400 rounded-full mb-1 opacity-40"></div>
                                    <span className="text-xs mt-1 text-blue-600 font-medium">{widget.levels?.low}</span>
                                </motion.div>

                                <motion.div className="flex flex-col items-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}>
                                    <motion.div
                                        className="w-4 h-4 bg-gradient-to-br from-rose-500 to-red-600 rounded-full mb-1 shadow-lg"
                                        animate={{
                                            scale: [1, 1.3, 1],
                                            boxShadow: ["0 0 0 0 rgba(244, 63, 94, 0.4)", "0 0 0 10px rgba(244, 63, 94, 0)", "0 0 0 0 rgba(244, 63, 94, 0)"],
                                        }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    ></motion.div>
                                    <span className="text-sm mt-1 text-rose-700 font-bold">{widget.levels?.normal}</span>
                                </motion.div>

                                <motion.div className="flex flex-col items-center" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }}>
                                    <div className="w-3 h-3 bg-red-400 rounded-full mb-1 opacity-40"></div>
                                    <span className="text-xs text-red-600 font-medium">{widget.levels?.high}</span>
                                </motion.div>
                            </div>

                            {/* Connection line with animation */}
                            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                                <motion.div
                                    className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-400 via-rose-500 to-red-500 rounded-full"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 1.5, delay: 0.8 }}
                                />

                                {/* Current status indicator */}
                                <motion.div
                                    className="absolute top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white border-4 border-rose-500 rounded-full shadow-lg"
                                    initial={{ left: "0%" }}
                                    animate={{ left: "50%" }}
                                    transition={{ type: "spring", stiffness: 100, damping: 15, delay: 1.2 }}
                                >
                                    <motion.div className="w-full h-full bg-rose-500 rounded-full" animate={{ scale: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }} />
                                </motion.div>
                            </div>

                            {/* Status message */}
                            <motion.div className="mt-4 text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}>
                                <p className="text-rose-700 mt-1 font-semibold">{widget.statusMessage}</p>
                            </motion.div>
                        </motion.div>
                    </div>

                    {/* Background particles */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {[...Array(6)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-1 h-1 bg-rose-300 rounded-full opacity-30"
                                animate={{ x: [0, 100, 0], y: [0, -50, 0], opacity: [0, 0.6, 0] }}
                                transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.5 }}
                                style={{ left: `${10 + i * 15}%`, top: `${20 + i * 10}%` }}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* AI Analysis widget */}
            <motion.div
                className="bg-gradient-to-br from-rose-50/60 to-red-50/40 backdrop-blur-xl rounded-2xl p-4 border border-rose-200/50 mb-3 shadow-lg hover:shadow-xl transition-all duration-500"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                whileHover={{ scale: 1.02 }}
            >
                <div className="flex items-center mb-2">
                    <motion.div
                        className={`w-10 h-10 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl flex items-center justify-center me-2 shadow-lg`}
                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                            <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor" />
                            <path d="M19 15L20.09 18.26L24 19L20.09 19.74L19 23L17.91 19.74L14 19L17.91 18.26L19 15Z" fill="currentColor" opacity="0.7" />
                        </svg>
                    </motion.div>
                    <div>
                        <h4 className="font-bold text-rose-900 text-lg">{widget.aiAnalysis?.title}</h4>
                        <p className="text-sm text-rose-600">{widget.aiAnalysis?.subtitle}</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <motion.div
                        className="flex items-center justify-between p-2 bg-white/60 backdrop-blur-sm rounded-xl border border-rose-100/50 shadow-sm"
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        whileHover={{ x: 5 }}
                    >
                        <div className="flex items-center">
                            <motion.div className="w-2 h-2 mt-0.5 bg-rose-500 rounded-full ml-1" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                            <span className="text-sm font-medium text-gray-800">{widget.aiAnalysis?.overall?.label}</span>
                        </div>
                        <motion.span
                            className="text-sm font-bold text-rose-700 bg-rose-100/70 px-3 py-1 rounded-lg"
                            animate={{ opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            {widget.aiAnalysis?.overall?.value}
                        </motion.span>
                    </motion.div>

                    <motion.div
                        className="flex items-center justify-between p-2 bg-white/60 backdrop-blur-sm rounded-xl border border-rose-100/50 shadow-sm"
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.7 }}
                        whileHover={{ x: 5 }}
                    >
                        <div className="flex items-center">
                            <motion.div className="w-2 h-2 mt-0.5 bg-rose-500 rounded-full ml-1" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }} />
                            <span className="text-sm font-medium text-gray-800">{widget.aiAnalysis?.weeklyTrend?.label}</span>
                        </div>
                        <motion.span
                            className="text-sm font-bold text-rose-700 bg-rose-100/70 px-3 py-1 rounded-lg"
                            animate={{ opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                        >
                            {widget.aiAnalysis?.weeklyTrend?.value}
                        </motion.span>
                    </motion.div>

                    <motion.div
                        className="flex items-center justify-between p-2 bg-white/60 backdrop-blur-sm rounded-xl border border-rose-100/50 shadow-sm"
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.9 }}
                        whileHover={{ x: 5 }}
                    >
                        <div className="flex items-center">
                            <motion.div className="w-2 h-2 mt-0.5 bg-rose-500 rounded-full ml-1" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }} />
                            <span className="text-sm font-medium text-gray-800">{widget.aiAnalysis?.prediction?.label}</span>
                        </div>
                        <motion.span
                            className="text-sm font-bold text-rose-700 bg-rose-100/70 px-3 py-1 rounded-lg"
                            animate={{ opacity: [0.8, 1, 0.8] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                        >
                            {widget.aiAnalysis?.prediction?.value}
                        </motion.span>
                    </motion.div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default GlucoseWidget;
